import { useCallback, useMemo } from "react";
import {
  InstrumentTypeResponse,
  OrderTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { TEXT_COLORS } from "../../../constants/design/colors";
import {
  INDEX_PRICE_COLLAR_OPTIONS,
  MARK_PRICE_COLLAR,
  MIN_ORDER_VALUE,
} from "../../../constants/precision/form";
import { AccountStateEnum } from "../../../contexts/AuthContext";
import { maxOrderSizeForMarkPriceCollar } from "../../../utils/orderbook";
import { knownPrice } from "../../../utils/unknownNumbers";
import { ClosePositionButton, LiquidationsText } from "../style";
import { Spinner } from "../../shared/Spinner";
import { useOptionsTradeFormSetup } from "./useOptionsTradeFormSetup";
import { useOptionsTradeFormComputed } from "./useOptionsTradeFormComputed";
import { useOptionsTradeFormPosition } from "./useOptionsTradeFormPosition";

type SetupResult = ReturnType<typeof useOptionsTradeFormSetup>;
type ComputedResult = ReturnType<typeof useOptionsTradeFormComputed>;
type PositionResult = ReturnType<typeof useOptionsTradeFormPosition>;

export function useOptionsTradeFormHandlers({
  setOrderSide,
  setValue,
  modalRef,
  orderType,
  price,
  currentPosition,
  orderSide,
  marginData,
  accountData,
  calculateTotalValueWithSize,
  accountSigningKeyState,
  showOnboarding,
  isLoading,
  commonFormTranslations,
  setShowCloseModal,
  market,
  orderbookData,
  ticker,
  index,
  insufficientMarketLiquidityWarning,
  setOrderType,
}: SetupResult & ComputedResult & PositionResult) {
  const onRowClick = useCallback(
    (p: string, size: string, side: SideResponse) => {
      setOrderSide(side);
      setValue("amount", size, { shouldValidate: true });
      setValue("price", p, { shouldValidate: true });

      // When the price is adjusted, modal scrolls back to the top
      if (modalRef.current) {
        modalRef.current.scrollIntoView({
          behavior: "smooth",
        });
      }
    },
    [setValue]
  );

  const onSetOrderSide = useCallback(
    (value: string) => setOrderSide(value as SideResponse),
    []
  );
  const onSetOrderType = useCallback(
    (value: string) => setOrderType(value as OrderTypeResponse),
    []
  );

  const verifyOrderValueTooSmall = useCallback(
    (contractSize: string) => {
      if (orderType === OrderTypeResponse.Limit && price && contractSize) {
        return (
          Number(contractSize || 0) * Number(price || 0) >= MIN_ORDER_VALUE
        );
      }
      return true;
    },
    [orderType, price]
  );

  const verifyNotEnoughBalance = useCallback(
    (size: string) => {
      if (
        currentPosition &&
        currentPosition.side !== orderSide &&
        Number(currentPosition.amount) >= Number(size)
      ) {
        return true;
      }

      if (orderSide === SideResponse.Sell && marginData) {
        return (
          Number(marginData.initial_margin) <=
          Number(accountData?.available_balance || 0)
        );
      }
      // A BUY spends free collateral, not the account's TOTAL balance: the
      // figure shown above this field is `available_balance` and the error
      // says "insufficient available balance", but the check read `balance`,
      // which includes margin already committed. A buy sized against
      // committed margin passed here and failed on-chain.
      return accountData && size
        ? Number(accountData.available_balance ?? 0) >=
            calculateTotalValueWithSize(size)
        : true;
    },
    [
      accountData,
      calculateTotalValueWithSize,
      currentPosition,
      marginData,
      orderSide,
    ]
  );

  const closePositionContent = useMemo(() => {
    if (accountSigningKeyState === AccountStateEnum.OK && !showOnboarding) {
      if (accountData?.in_liquidation) {
        return (
          <LiquidationsText>
            {commonFormTranslations("liquidations_in_progress")}
          </LiquidationsText>
        );
      }
      return (
        <ClosePositionButton
          type={"button"}
          onClick={() => setShowCloseModal(true)}
          disabled={!currentPosition}
        >
          {!isLoading ? (
            commonFormTranslations("close_position")
          ) : (
            <Spinner color={TEXT_COLORS.two} />
          )}
        </ClosePositionButton>
      );
    }

    return null;
  }, [
    accountSigningKeyState,
    showOnboarding,
    accountData?.in_liquidation,
    currentPosition,
    isLoading,
    commonFormTranslations,
  ]);

  // The collar needs both prices. With either unknown it cannot be computed:
  // a band around 0 capped every market order at (almost) nothing.
  const collarMark = knownPrice(ticker?.mark?.price);
  const collarIndex = knownPrice(index?.price);
  const maxOrderSizeBeforeOBProtectionTriggered =
    orderType === OrderTypeResponse.Market &&
    market.derivative !== InstrumentTypeResponse.Perpetual &&
    !insufficientMarketLiquidityWarning &&
    collarMark !== undefined &&
    collarIndex !== undefined
      ? maxOrderSizeForMarkPriceCollar(
          orderSide,
          orderbookData?.bids || [],
          orderbookData?.asks || [],
          collarMark,
          collarIndex,
          MARK_PRICE_COLLAR,
          INDEX_PRICE_COLLAR_OPTIONS
        )
      : Number.MAX_VALUE;

  const feeStructure = accountData?.fee_structures?.find(
    (f) => f.asset === market.asset && f.instrument_type === market.derivative
  );

  return {
    onRowClick,
    onSetOrderSide,
    onSetOrderType,
    verifyOrderValueTooSmall,
    verifyNotEnoughBalance,
    closePositionContent,
    maxOrderSizeBeforeOBProtectionTriggered,
    feeStructure,
  };
}
